import React, { useMemo } from 'react'
import {
  alpha,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  FormHelperText,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CategoryIcon from '@mui/icons-material/Category'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import TuneIcon from '@mui/icons-material/Tune'
import type { ExpenseSubcategoryDoc, RepairCategoryDoc } from '@otw/models'
import { OTHER_REPAIR_CATEGORY_KEY } from '@otw/models'
import { getRepairCategoryIcon } from './repairCategoryIcons'

export interface RepairCategorySelectProps {
  value: string
  onChange: (categoryKey: string) => void
  categories: RepairCategoryDoc[]
  subcategory?: string
  onSubcategoryChange?: (subcategory: string) => void
  subcategories?: ExpenseSubcategoryDoc[]
  onAddSubcategory?: () => void
  onManage?: () => void
  error?: boolean
  helperText?: string
  subcategoryError?: boolean
  subcategoryHelperText?: string
}

const resolveSubcategoryCategoryKey = (sub: ExpenseSubcategoryDoc): string =>
  sub.categoryKey?.trim() || OTHER_REPAIR_CATEGORY_KEY

/** Category + subcategory picker aligned with RepairCategoryPicker / ExpenseDialog taxonomy. */
export const RepairCategorySelect: React.FC<RepairCategorySelectProps> = ({
  value,
  onChange,
  categories,
  subcategory = '',
  onSubcategoryChange,
  subcategories = [],
  onAddSubcategory,
  onManage,
  error = false,
  helperText,
  subcategoryError = false,
  subcategoryHelperText,
}) => {
  const theme = useTheme()

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [categories],
  )

  const tabValue = sortedCategories.some(c => c.key === value)
    ? value
    : sortedCategories[0]?.key ?? ''

  const activeCategory = sortedCategories.find(c => c.key === tabValue)

  const subcategoriesForCategory = useMemo(
    () =>
      subcategories
        .filter(sub => resolveSubcategoryCategoryKey(sub) === tabValue)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [subcategories, tabValue],
  )

  const showSubcategories = Boolean(onSubcategoryChange)

  return (
    <Box>
      <Box
        sx={{
          p: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, error || subcategoryError ? 1 : 0.2)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <CategoryIcon color={error || subcategoryError ? 'error' : 'primary'} fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Repair taxonomy
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {onManage && (
            <Tooltip title="Manage categories & subcategories">
              <IconButton size="small" onClick={onManage}>
                <TuneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Category
        </Typography>

        <Tabs
          value={tabValue}
          onChange={(_, next) => onChange(next as string)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 56,
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontSize: '0.72rem',
              px: 1.25,
              py: 0.5,
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: 'primary.main' },
            '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
          }}
        >
          {sortedCategories.map(cat => {
            const { Icon } = getRepairCategoryIcon(cat.iconKey)
            return (
              <Tab
                key={cat.key}
                value={cat.key}
                icon={<Icon fontSize="small" />}
                label={cat.label}
                iconPosition="top"
              />
            )
          })}
        </Tabs>

        {showSubcategories ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Subcategory
              {activeCategory ? ` · ${activeCategory.label}` : ''}
            </Typography>

            {subcategoriesForCategory.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 0.5, px: 0.5 }}>
                No subcategories in {activeCategory?.label ?? 'this category'} yet.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {subcategoriesForCategory.map(sub => {
                  const isSelected = subcategory === sub.label
                  const { Icon } = getRepairCategoryIcon(activeCategory?.iconKey)
                  return (
                    <Card
                      key={sub.id}
                      variant="outlined"
                      sx={{
                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                      }}
                    >
                      <CardActionArea onClick={() => onSubcategoryChange?.(sub.label)}>
                        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                          <Stack direction="row" alignItems="center" spacing={1.25}>
                            <Box sx={{ color: isSelected ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                              <Icon fontSize="small" />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ flex: 1, fontWeight: isSelected ? 700 : 500, color: 'text.primary' }}
                            >
                              {sub.label}
                            </Typography>
                            <Chip
                              label={isSelected ? 'Selected' : 'Select'}
                              size="small"
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                              icon={isSelected ? <CheckCircleIcon fontSize="small" /> : undefined}
                              sx={{ height: 22, flexShrink: 0 }}
                            />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  )
                })}
              </Stack>
            )}

            {onAddSubcategory ? (
              <Card
                variant="outlined"
                sx={{ mt: 0.75, borderStyle: 'dashed', borderColor: 'divider' }}
              >
                <CardActionArea onClick={onAddSubcategory}>
                  <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AddIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Add subcategory to {activeCategory?.label ?? 'category'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            ) : null}
          </Box>
        ) : null}
      </Box>
      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 1.75, mt: 0.5 }}>
          {helperText}
        </FormHelperText>
      ) : null}
      {subcategoryHelperText ? (
        <FormHelperText error={subcategoryError} sx={{ mx: 1.75, mt: helperText ? 0 : 0.5 }}>
          {subcategoryHelperText}
        </FormHelperText>
      ) : null}
    </Box>
  )
}

export default RepairCategorySelect
